import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Package, Scan, X, Check, AlertCircle, 
  History, RefreshCw, Trash2, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const LocationTransfer = () => {
  const navigate = useNavigate();
  const bundleInputRef = useRef(null);
  const locationInputRef = useRef(null);
  
  // Form state
  const [newLocation, setNewLocation] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // History dialog
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    // Focus on location input first
    locationInputRef.current?.focus();
  }, []);

  // Auto-focus bundle input when location is set and after bundles list changes
  useEffect(() => {
    if (newLocation.trim() && !isProcessing && !isSubmitting) {
      // Use requestAnimationFrame + setTimeout for reliable focus after React state updates
      const focusBundle = () => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (bundleInputRef.current && !bundleInputRef.current.disabled) {
              bundleInputRef.current.focus();
              bundleInputRef.current.select(); // Also select any existing text
            }
          }, 50);
        });
      };
      focusBundle();
    }
  }, [newLocation, scannedBundles.length, isProcessing, isSubmitting]);

  const handleLocationSubmit = (e) => {
    e.preventDefault();
    if (newLocation.trim()) {
      // Move focus to bundle input
      bundleInputRef.current?.focus();
    }
  };

  // Helper function to focus bundle input reliably
  const focusBundleInput = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (bundleInputRef.current && !bundleInputRef.current.disabled) {
          bundleInputRef.current.focus();
        }
      }, 50);
    });
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleCode.trim().toUpperCase();
    
    if (!code) return;
    
    // Check if already scanned
    if (scannedBundles.some(b => b.bundle_code === code)) {
      toast.error('Bundle already scanned');
      setBundleCode('');
      // Refocus after error
      focusBundleInput();
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const response = await apiClient.get(`/goods-transfer/location-transfer/validate-bundle/${encodeURIComponent(code)}`);
      
      if (response.data.valid) {
        setScannedBundles(prev => [...prev, {
          bundle_code: code,
          current_location: response.data.current_location,
          usn_count: response.data.usn_count,
          design_name: response.data.design_name,
          shade: response.data.shade,
          size: response.data.size
        }]);
        toast.success(`Bundle ${code} added`);
        setBundleCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid bundle code');
      setBundleCode('');
    } finally {
      setIsProcessing(false);
      // Explicitly refocus bundle input after processing completes
      // Using longer delay to ensure state updates are complete
      setTimeout(() => {
        if (bundleInputRef.current && !bundleInputRef.current.disabled) {
          bundleInputRef.current.focus();
        }
      }, 150);
    }
  };

  const removeBundle = (bundleCode) => {
    setScannedBundles(prev => prev.filter(b => b.bundle_code !== bundleCode));
  };

  const handleSubmit = async () => {
    if (!newLocation.trim()) {
      toast.error('Please enter the new location');
      locationInputRef.current?.focus();
      return;
    }
    
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      bundleInputRef.current?.focus();
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await apiClient.post('/goods-transfer/location-transfer', {
        new_location: newLocation.trim(),
        bundle_codes: scannedBundles.map(b => b.bundle_code)
      });
      
      if (response.data.success) {
        toast.success(`Transfer ${response.data.transfer_id} completed! ${response.data.bundles_transferred} bundles moved to ${newLocation}`);
        
        // Reset form
        setNewLocation('');
        setScannedBundles([]);
        setBundleCode('');
        locationInputRef.current?.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get('/goods-transfer/location-transfer/history?limit=20');
      setHistory(response.data.transfers || []);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const clearAll = () => {
    setScannedBundles([]);
    setBundleCode('');
    focusBundleInput();
  };

  // Calculate totals
  const totalUsns = scannedBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/goods-transfer')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-[#E8DCC8]" />
              <div>
                <h1 className="text-xl font-bold font-display">Location Transfer</h1>
                <p className="text-sm text-[#E8DCC8]">Move bundles to a new location</p>
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={openHistory}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="history-button"
          >
            <History className="w-5 h-5 mr-2" />
            History
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* New Location Input */}
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              New Location (Destination)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLocationSubmit}>
              <div className="flex gap-3">
                <Input
                  ref={locationInputRef}
                  type="text"
                  placeholder="Enter new location (e.g., Rack A-01, Shelf B-02)"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  className="flex-1 text-lg h-12"
                  data-testid="new-location-input"
                />
                {newLocation && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 h-12 px-4 flex items-center text-base">
                    {newLocation}
                  </Badge>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Bundle Scanner */}
        <Card className="mb-6 border-2 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scan className="w-5 h-5 text-green-600" />
              Scan Bundles to Transfer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBundleScan}>
              <div className="flex gap-3">
                <Input
                  ref={bundleInputRef}
                  type="text"
                  placeholder="Scan or enter bundle barcode"
                  value={bundleCode}
                  onChange={(e) => setBundleCode(e.target.value)}
                  disabled={isProcessing || !newLocation.trim()}
                  className="flex-1 text-lg h-12 font-mono"
                  data-testid="bundle-input"
                  autoComplete="off"
                  onBlur={(e) => {
                    // Re-focus if the user accidentally clicked away but location is still set
                    if (newLocation.trim() && !isProcessing && !isSubmitting) {
                      // Only refocus if blur wasn't caused by clicking a button
                      const relatedTarget = e.relatedTarget;
                      if (!relatedTarget || !relatedTarget.closest('button')) {
                        focusBundleInput();
                      }
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  disabled={isProcessing || !bundleCode.trim() || !newLocation.trim()}
                  className="bg-green-600 hover:bg-green-700 h-12 px-6"
                >
                  {isProcessing ? 'Validating...' : 'Add'}
                </Button>
              </div>
              {!newLocation.trim() && (
                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Enter the new location first before scanning bundles
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Scanned Bundles List */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-600" />
                Scanned Bundles ({scannedBundles.length})
              </CardTitle>
              {scannedBundles.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearAll}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {scannedBundles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No bundles scanned yet</p>
                <p className="text-sm">Scan bundles to add them to the transfer</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {scannedBundles.map((bundle, idx) => (
                  <div 
                    key={bundle.bundle_code}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    data-testid={`scanned-bundle-${bundle.bundle_code}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-mono font-medium text-[#1A1F2E]">{bundle.bundle_code}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>From: <strong>{bundle.current_location || 'Not assigned'}</strong></span>
                          <span>•</span>
                          <span>{bundle.usn_count} USNs</span>
                          {bundle.design_name && (
                            <>
                              <span>•</span>
                              <span>{bundle.design_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBundle(bundle.bundle_code)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {scannedBundles.length > 0 && (
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-gray-500">Total Bundles:</span>
                    <span className="ml-2 font-bold text-lg">{scannedBundles.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total USNs:</span>
                    <span className="ml-2 font-bold text-lg">{totalUsns}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">New Location:</span>
                    <span className="ml-2 font-bold text-lg text-blue-600">{newLocation || '-'}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || scannedBundles.length === 0 || !newLocation.trim()}
          className="w-full h-14 text-lg bg-[#2D5F4F] hover:bg-[#234A3D]"
          data-testid="submit-transfer"
        >
          {isSubmitting ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Processing Transfer...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Transfer {scannedBundles.length} Bundle{scannedBundles.length !== 1 ? 's' : ''} to {newLocation || '...'}
            </>
          )}
        </Button>
      </main>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Location Transfer History
            </DialogTitle>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No transfer history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((transfer) => (
                <div 
                  key={transfer.transfer_id}
                  className="p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="font-mono">
                      {transfer.transfer_id}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {new Date(transfer.transferred_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">To:</span>
                      <span className="ml-1 font-medium text-blue-600">{transfer.new_location}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Bundles:</span>
                      <span className="ml-1 font-medium">{transfer.bundle_count}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">USNs:</span>
                      <span className="ml-1 font-medium">{transfer.total_usn_count}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">By:</span>
                      <span className="ml-1 font-medium">{transfer.transferred_by_name}</span>
                    </div>
                  </div>
                  {transfer.bundles && transfer.bundles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {transfer.bundles.slice(0, 5).map((b) => (
                        <Badge key={b.bundle_code} variant="secondary" className="text-xs font-mono">
                          {b.bundle_code}
                        </Badge>
                      ))}
                      {transfer.bundles.length > 5 && (
                        <Badge variant="secondary" className="text-xs">
                          +{transfer.bundles.length - 5} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationTransfer;
